/**
 * API client functions for the Access Manager admin UI.
 *
 * All calls go through the shared apiClient (which handles auth headers, HMAC
 * signing, retry policy, etc.) and use endpoint paths from the central config.
 */

import apiClient from "../../utils/axiosConfig";
import { getApiUrl, API_ENDPOINTS } from "../../Configurations/config";

/**
 * Fetches a summary of user access data.
 *
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function getSummary() {
  return apiClient.get(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_SUMMARY));
}

/**
 * Fetches the list of users and their current access scopes.
 *
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function listUsers() {
  return apiClient.get(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_USERS));
}

/**
 * Fetches the grant list.
 *
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function listGrants() {
  return apiClient.get(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_LIST));
}

/**
 * Grants a scope to a user.
 *
 * @param {string} email - The user's email address.
 * @param {string} scope - The scope to grant (e.g. "admin", "supervisor", "semantic").
 * @param {string} [notes] - Optional notes documenting the reason for the grant.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function grantScope(email, scope, notes) {
  return apiClient.post(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_GRANT), { email, scope, notes });
}

/**
 * Revokes a scope from a user.
 *
 * @param {string} email - The user's email address.
 * @param {string} scope - The scope to revoke.
 * @param {string} [notes] - Optional notes documenting the reason for the revocation.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function revokeScope(email, scope, notes) {
  return apiClient.post(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_REVOKE), { email, scope, notes });
}

/**
 * Sets scopes for a user (replaces all existing scopes).
 *
 * @param {string} email - The user's email address.
 * @param {string[]} scopes - The scopes to set.
 * @param {string} [notes] - Optional notes documenting the reason for the change.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function setScopes(email, scopes, notes) {
  return apiClient.put(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_SCOPES), { email, scopes, notes });
}

/**
 * Fetches scopes for a specific user.
 *
 * @param {string} email - The user's email address.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function getUserScopes(email) {
  return apiClient.get(getApiUrl(`${API_ENDPOINTS.ADMIN.ACCESS_USER_SCOPES}/${encodeURIComponent(email)}/scopes`));
}

/**
 * Fetches recent audit events for access changes.
 *
 * @param {Object} options
 * @param {number} [options.limit] - Maximum number of events to return.
 * @param {string} [options.email] - Filter events by target email address.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function listAuditEvents({ limit, email } = {}) {
  const params = {};
  if (limit != null) {
    params.limit = limit;
  }
  if (email) {
    params.email = email;
  }
  return apiClient.get(getApiUrl(API_ENDPOINTS.ADMIN.ACCESS_AUDIT), { params });
}
